import type { TimelineItemDto } from '@ovo/shared'
import { formatDue, formatMoment } from '../lib/dates.ts'
import { shortName } from '../lib/status.ts'
import { Avatar } from './Avatar.tsx'
import { StatusGlyph } from './StatusBadge.tsx'

type EventItem = Extract<TimelineItemDto, { kind: 'event' }>
type CommentItem = Extract<TimelineItemDto, { kind: 'comment' }>

const COMMENT_KIND_LABELS: Record<string, string> = {
  return: 'На доработку',
  block: 'Блокер',
  decline: 'Причина отказа',
  question: 'Вопрос',
  answer: 'Ответ',
  result: 'Результат',
}

function dateFrom(data: Record<string, unknown>, key: string): string | null {
  const v = data[key]
  return typeof v === 'string' ? formatDue(v) : null
}

/** Нейтральные формулировки событий (без рода и без обвинений). */
function eventText(e: EventItem): string | null {
  switch (e.type) {
    case 'created':
      return 'Задача поставлена'
    case 'accepted':
      return 'Принята в работу'
    case 'declined':
      return 'Отклонена'
    case 'started':
      return 'Работа начата'
    case 'blocked':
      return 'Отмечен блокер'
    case 'unblocked':
      return 'Блокер снят'
    case 'submitted':
      return e.toStatus === 'done' ? 'Отмечена выполненной' : 'Сдана на проверку'
    case 'verified':
      return 'Работа принята'
    case 'returned':
      return 'Возвращена на доработку'
    case 'due_requested': {
      const d = dateFrom(e.data, 'proposedDueAt')
      return d ? `Предложен новый срок: ${d}` : 'Предложен новый срок'
    }
    case 'due_approved': {
      const d = dateFrom(e.data, 'proposedDueAt')
      return d ? `Новый срок согласован: ${d}` : 'Новый срок согласован'
    }
    case 'due_rejected':
      return 'Перенос срока не согласован'
    case 'question_asked':
      return null // сам вопрос показан комментарием
    case 'question_answered':
      return null
    case 'due_changed': {
      const d = dateFrom(e.data, 'to')
      return d ? `Срок изменён: ${d}` : 'Срок снят'
    }
    case 'edited':
      return 'Задача изменена'
    case 'reassigned':
      return 'Исполнитель изменён'
    case 'cancelled':
      return 'Задача отменена'
    case 'reopened':
      return 'Возвращена в работу'
    case 'on_track':
      return 'Отмечено: всё по плану'
    case 'snoozed':
      return 'Напоминание отложено'
    case 'commented':
      return null
    default:
      return 'Изменение задачи'
  }
}

function EventRow({ e, meId, now }: { e: EventItem; meId: number; now: Date }) {
  const text = eventText(e)
  if (!text) return null
  return (
    <li className="timeline__event">
      <span className="timeline__dot">
        {e.toStatus ? <StatusGlyph status={e.toStatus} size={16} /> : <span aria-hidden="true">·</span>}
      </span>
      <span className="timeline__text">
        <b>{text}</b>
        {' · '}
        {e.actor ? `${e.actor.id === meId ? 'вы' : shortName(e.actor.displayName)}, ` : ''}
        <time dateTime={e.at}>{formatMoment(e.at, now)}</time>
      </span>
    </li>
  )
}

function CommentRow({ c, meId, now }: { c: CommentItem; meId: number; now: Date }) {
  const label = COMMENT_KIND_LABELS[c.commentKind]
  const mine = c.actor?.id === meId
  const tone =
    c.commentKind === 'result'
      ? ' bubble--result'
      : c.commentKind === 'block'
        ? ' bubble--attn'
        : mine
          ? ' bubble--mine'
          : ''
  return (
    <li className="timeline__comment">
      <Avatar name={c.actor?.displayName ?? '?'} id={c.actor?.id ?? 0} size={28} />
      <div className={`bubble${tone}`}>
        <div className="bubble__head">
          <span className="bubble__author">
            {c.actor ? (mine ? 'Вы' : shortName(c.actor.displayName)) : 'Система'}
          </span>
          {label && <span className="bubble__kind">{label}</span>}
          <time className="bubble__time" dateTime={c.at}>
            {formatMoment(c.at, now)}
          </time>
        </div>
        <div className="bubble__body">{c.body}</div>
      </div>
    </li>
  )
}

/** Лента задачи: события статусов и комментарии в хронологическом порядке. */
export function Timeline({ items, meId }: { items: TimelineItemDto[]; meId: number }) {
  const now = new Date()
  const sorted = [...items].sort((a, b) => a.at.localeCompare(b.at))
  return (
    <ol className="timeline">
      {sorted.map((item) =>
        item.kind === 'event' ? (
          <EventRow key={item.id} e={item} meId={meId} now={now} />
        ) : (
          <CommentRow key={item.id} c={item} meId={meId} now={now} />
        ),
      )}
    </ol>
  )
}
